import { default as axiosInstance } from '@utils/interceptor'

import {
  IGfpaDetailsDTO,
  IGfpaDurations,
  IGfpaFactorContribution,
  IGfpaSummaryDTO,
  IGfpaTotalReturn,
  IReportSelectionParameters,
  IReportsColumnDef,
} from '@/interfaces/gfpaReport.interface'
import {
  IGfrDetailsDTO,
  IGfrRiskHistoryDTO,
  IGfrRiskOverView,
} from '@/interfaces/gfrReport.interface'
import { IReportDTO2 } from '@/interfaces/report.interface'
import { IRvSecurityDetailsData } from '@/interfaces/rvDetails.interface'
import {
  IReturnVarianceReportDailyAttribution,
  IReturnVarianceReportOverview,
  IRvAttributionBySectorDTO,
} from '@/interfaces/rvReport.interface'
import {
  IScenarioAnalysisReportOverviewDTO,
  IScenarioSecurityDetailDTO,
  IScheduledReportsDTO,
  IScheduledReportsDTO2,
} from '@/interfaces/scenarioAnalysisReportComponents.interface'
import { IScheduleReportSimpleReturn2 } from '@/interfaces/scenarioBuilder.interface'

export const getAllReports = (): Promise<IReportDTO2[]> => {
  return axiosInstance
    .get<IReportDTO2[]>('reports')
    .then((response: any) => response.data)
}

export const getGFPASummaryReport = async (
  id: number,
): Promise<IGfpaSummaryDTO[]> => {
  const response = await axiosInstance.get(`gfpa/api/v1/summary?reportID=${id}`)
  return response.data as IGfpaSummaryDTO[]
}

export const getGFRSummaryReport = async (
  id: number,
  beginDate: string,
  endDate: string,
): Promise<any> => {
  const response = await axiosInstance.get(
    `gfr/api/v1/riskHistory?reportId=${id}&beginDate=${beginDate}&endDate=${endDate}`,
  )
  return response.data as IGfrRiskHistoryDTO[]
}

export const getGFPADetailsReport = async (
  id: number,
  seriesName: string,
): Promise<any> => {
  const response = await axiosInstance.get(
    `gfpa/api/v1/security-details?reportId=${id}&seriesName=${seriesName}`,
  )
  return response.data as IGfpaDetailsDTO[]
}

export const fetchReportFiltersAsync = async (
  type: string,
  portfolio?: string,
  benchmark?: string,
  template?: string,
  date?: string,
): Promise<any> => {
  let url = `report/api/v1/selection`
  if (type && !portfolio && !benchmark && !template && !date) {
    url = `${url}?reportType=${type}&requestedField=REPORT_SELECTION_PORTFOLIO_NAME`
  } else if (type && portfolio && !benchmark && !template && !date) {
    url = `${url}?reportType=${type}&portfolioName=${portfolio}&requestedField=REPORT_SELECTION_BENCHMARK_NAME`
  } else if (type && portfolio && benchmark && !template && !date) {
    url = `${url}?reportType=${type}&portfolioName=${portfolio}&benchmarkName=${benchmark}&requestedField=REPORT_SELECTION_TEMPLATE_NAME`
  } else if (type && portfolio && benchmark && template && !date) {
    url = `${url}?reportType=${type}&portfolioName=${portfolio}&benchmarkName=${benchmark}&templateName=${template}&requestedField=REPORT_SELECTION_DATE_RANGE`
  } else if (type && portfolio && benchmark && template && date) {
    url = `${url}?reportType=${type}&portfolioName=${portfolio}&benchmarkName=${benchmark}&templateName=${template}&dateRange=${date}`
  }
  const response = await axiosInstance.get(url)
  return response.data as IReportSelectionParameters
}

export const getGFPAFactorContributionReport = async (
  id: number,
  type: string,
  beginDate?: number,
  endDate?: number,
): Promise<IGfpaFactorContribution[]> => {
  // Use 'gfpa' endpoint for both GFPA and GFPAH reports
  const apiType = /^gfpah?$/i.test(type) ? 'gfpa' : type.toLowerCase()
  let url = `${apiType}/api/v1/factor-contribution?reportId=${id}`
  // Optional date range (yyyyMMdd); omitted callers keep the existing full-range behavior.
  if (beginDate) url += `&beginDate=${beginDate}`
  if (endDate) url += `&endDate=${endDate}`
  const response = await axiosInstance.get(url)
  return response.data as IGfpaFactorContribution[]
}

export const getReportTotalReturns = async (
  type: string,
  id: number,
): Promise<IGfpaTotalReturn> => {
  const response = await axiosInstance.get(
    `report/api/v1/totalReturn?reportType=${type}&reportId=${id}`,
  )
  return response.data as IGfpaTotalReturn
}

export const getReportDuration = async (
  type: string,
  id: number,
): Promise<IGfpaDurations> => {
  const response = await axiosInstance.get(
    `report/api/v1/duration?reportType=${type}&reportId=${id}`,
  )
  return response.data as IGfpaDurations
}

export const getRiskOverview = async (
  id: number,
): Promise<IGfrRiskOverView> => {
  const response = await axiosInstance.get(
    `/gfr/api/v1/risk-overview?reportId=${id}`,
  )
  return response?.data as IGfrRiskOverView
}

export const getGFRDetailsReport = async (
  id: number,
  seriesName: string,
): Promise<any> => {
  const response = await axiosInstance.get(
    `gfr/api/v1/security-details?reportId=${id}&seriesName=${seriesName}`,
  )
  return response.data as IGfrDetailsDTO[]
}

export const getReturnVarianceReportOverview = async (
  id: number,
): Promise<IReturnVarianceReportOverview> => {
  const response = await axiosInstance.get(
    `/returnVariance/api/v1/overview?reportID=${id}`,
  )
  return response.data as IReturnVarianceReportOverview
}

export const getReturnVarianceReportDailyAttribution = async (
  id: number,
): Promise<IReturnVarianceReportDailyAttribution[]> => {
  const response = await axiosInstance.get(
    `/returnVariance/api/v1/dailyAttribution?reportId=${id}`,
  )
  return response.data as IReturnVarianceReportDailyAttribution[]
}

export const getAttributionBySector = async (
  id: number,
): Promise<IRvAttributionBySectorDTO[]> => {
  const response = await axiosInstance.get(
    `returnVariance/api/v1/attributionBySector?reportID=${id}`,
  )
  return response.data as IRvAttributionBySectorDTO[]
}

export const getRvSecurityDetailsReport = async (
  reportId: number,
): Promise<IRvSecurityDetailsData[]> => {
  const response = await axiosInstance.get(
    `returnVariance/api/v1/securityDetails?reportID=${reportId}`,
  )

  return response.data as IRvSecurityDetailsData[]
  //return rvSecurityDetailsMockData as IRvSecurityDetailsData[]
}

export const getColumnDefByReportType = async (
  type: string,
): Promise<IReportsColumnDef[]> => {
  const response = await axiosInstance.get(
    `report/api/v1/reportItems/metadata?reportType=${type}`,
  )

  return response.data as IReportsColumnDef[]
}

export const getScenarioAnalysisOverViewReport = async (
  reportId: number,
): Promise<IScenarioAnalysisReportOverviewDTO[]> => {
  const response = await axiosInstance.get(
    `scenario/api/v1/overview?reportId=${reportId}`,
  )
  return response.data as IScenarioAnalysisReportOverviewDTO[]
}

export const getScenarioSecurityDetailReport = async (
  reportId: number,
): Promise<IScenarioSecurityDetailDTO[]> => {
  const response = await axiosInstance.get(
    `scenario/api/v1/securityDetails?reportId=${reportId}`,
  )
  return response.data as IScenarioSecurityDetailDTO[]
}

// TODO: This should be able to be removed.
export const getScheduledReports = async (): Promise<
  IScheduledReportsDTO[]
> => {
  const response = await axiosInstance.get('reports/api/v1/scheduledReports')
  return response.data as IScheduledReportsDTO[]
}

export const getScheduledReports2 = async (): Promise<
  IScheduledReportsDTO2[]
> => {
  const response = await axiosInstance.get('reportTemplateRun/schedules')
  return response.data as IScheduledReportsDTO2[]
}

export const deleteScheduledReport2 = async (id: number) => {
  const response = await axiosInstance.delete(
    `/reportTemplateRun/schedule/${id}`,
  )
  return response.data as IScheduleReportSimpleReturn2
}
